import React, { useState } from 'react';
import { fetchTowns } from '../utils/api';

export default function StepLocations({ data, onChange, sectors, onSectorsChange, onBack, onNext }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newTown, setNewTown] = useState('');
  const [newSector, setNewSector] = useState('');
  const [mode, setMode] = useState(data.mode || 'postcode'); // 'postcode' | 'manual'
  const [pasteInput, setPasteInput] = useState('');

  const towns = data.towns || [];
  const selectedTowns = data.selectedTowns || towns;

  const updateField = (field, val) => onChange({ ...data, [field]: val });

  const loadTowns = async () => {
    if (!data.postcode?.trim()) { setError('Please enter a postcode first.'); return; }
    setError('');
    setLoading(true);
    try {
      const res = await fetchTowns(data.postcode.trim(), data.radius || 10);
      onChange({ ...data, towns: res.towns, selectedTowns: res.towns, centre: res.centre, formattedAddress: res.formattedAddress, mode });
      if (res.warning) setError(res.warning);
    } catch (e) {
      setError('Could not fetch towns — check your postcode and try again.');
    }
    setLoading(false);
  };

  const parsePastedTowns = () => {
    const parsed = pasteInput
      .split(/[\n,;]+/)
      .map(t => t.trim())
      .filter(t => t.length > 0)
      .filter((t, i, arr) => arr.indexOf(t) === i);
    if (!parsed.length) { setError('No towns found — check your list and try again.'); return; }
    onChange({ ...data, towns: parsed, selectedTowns: parsed, mode });
    setPasteInput('');
    setError('');
  };

  const toggleTown = (town) => {
    const isSelected = selectedTowns.includes(town);
    updateField('selectedTowns', isSelected ? selectedTowns.filter(t => t !== town) : [...selectedTowns, town]);
  };

  const selectAll = () => updateField('selectedTowns', [...towns]);
  const selectNone = () => updateField('selectedTowns', []);

  const removeTown = (town) => {
    onChange({ ...data, towns: towns.filter(t => t !== town), selectedTowns: selectedTowns.filter(t => t !== town) });
  };

  const addTown = () => {
    const v = newTown.trim();
    if (v && !towns.includes(v)) {
      onChange({ ...data, towns: [...towns, v], selectedTowns: [...selectedTowns, v] });
      setNewTown('');
    }
  };

  const addSector = () => {
    const v = newSector.trim();
    if (v && !sectors.includes(v)) { onSectorsChange([...sectors, v]); setNewSector(''); }
  };

  const removeSector = (i) => onSectorsChange(sectors.filter((_, idx) => idx !== i));

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    onChange({ ...data, towns: [], selectedTowns: [], mode: newMode });
  };

  const valid = selectedTowns.length > 0;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Location targeting</div>
        <div className="page-sub">Set the client's service area. Selected towns will be appended to every approved keyword.</div>
      </div>

      <div className="card">
        <div className="card-title">Service area</div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {[
            { val: 'postcode', label: '📍 Postcode radius', desc: 'Auto-fetch towns from a postcode' },
            { val: 'manual', label: '📋 Paste town list', desc: 'Paste or type towns directly' },
          ].map(opt => (
            <label key={opt.val} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: `1px solid ${mode === opt.val ? 'var(--blue-border)' : 'var(--gray-200)'}`, background: mode === opt.val ? 'var(--blue-light)' : 'var(--gray-50)' }}>
              <input type="radio" name="locationMode" value={opt.val} checked={mode === opt.val} onChange={() => switchMode(opt.val)} style={{ marginTop: 2 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>

        {/* Postcode mode */}
        {mode === 'postcode' && (
          <>
            <div className="card-sub">Enter the client's postcode and working radius to auto-fetch nearby towns.</div>
            <div className="form-row form-row-2 form-group">
              <div>
                <label>Primary postcode *</label>
                <input type="text" placeholder="e.g. BR3 1PP" value={data.postcode || ''} onChange={e => updateField('postcode', e.target.value)} onKeyDown={e => e.key === 'Enter' && loadTowns()} />
              </div>
              <div>
                <label>Working radius</label>
                <select value={data.radius || 10} onChange={e => updateField('radius', parseInt(e.target.value))}>
                  <option value={1}>1 mile</option>
                  <option value={5}>5 miles</option>
                  <option value={10}>10 miles</option>
                  <option value={15}>15 miles</option>
                  <option value={20}>20 miles</option>
                  <option value={30}>30 miles</option>
                  <option value={50}>50 miles</option>
                </select>
              </div>
            </div>
            <button className="btn" onClick={loadTowns} disabled={loading || !data.postcode?.trim()}>
              {loading ? <><span className="spinner" /> Fetching towns…</> : 'Fetch towns within radius'}
            </button>
          </>
        )}

        {/* Manual paste mode */}
        {mode === 'manual' && (
          <>
            <div className="card-sub">Paste a list of towns — one per line, or separated by commas or semicolons.</div>
            <textarea
              rows={7}
              placeholder={'Bromley\nBeckenham\nOrpington\nChislehurst\nPetts Wood\nDowne\nKeston'}
              value={pasteInput}
              onChange={e => setPasteInput(e.target.value)}
              style={{ marginBottom: 10, fontFamily: 'inherit' }}
            />
            <button className="btn" onClick={parsePastedTowns} disabled={!pasteInput.trim()}>
              Add towns →
            </button>
          </>
        )}

        {error && <div className="alert alert-warning" style={{ marginTop: 12 }}>{error}</div>}

        {/* Town chips — shown in both modes once towns are loaded */}
        {towns.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, marginBottom: 8 }}>
              <div className="section-label" style={{ margin: 0 }}>
                Towns — {selectedTowns.length} of {towns.length} selected
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-sm" onClick={selectAll}>All</button>
                <button className="btn btn-sm" onClick={selectNone}>None</button>
              </div>
            </div>
            <div className="chip-grid">
              {towns.map((t, i) => {
                const isSelected = selectedTowns.includes(t);
                return (
                  <span key={i} className={`chip ${isSelected ? 'selected' : ''}`} style={{ paddingRight: 6 }}>
                    <span onClick={() => toggleTown(t)}>{t}</span>
                    <span onClick={() => removeTown(t)} style={{ marginLeft: 4, opacity: 0.4, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>×</span>
                  </span>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <input type="text" placeholder="Add a town manually…" value={newTown} onChange={e => setNewTown(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTown()} style={{ maxWidth: 220 }} />
              <button className="btn btn-sm" onClick={addTown} disabled={!newTown.trim()}>Add</button>
            </div>
          </>
        )}
      </div>

      {/* Sectors */}
      <div className="card">
        <div className="card-title">
          Sectors / niches{' '}
          <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--gray-500)' }}>optional</span>
        </div>
        <div className="card-sub">
          Does this client target specific sectors? e.g. "commercial", "landlords", "schools", "offices".
          Keywords will also be generated for each sector.
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input type="text" placeholder="e.g. commercial, landlords, schools" value={newSector} onChange={e => setNewSector(e.target.value)} onKeyDown={e => e.key === 'Enter' && addSector()} style={{ flex: 1 }} />
          <button className="btn btn-sm" onClick={addSector} disabled={!newSector.trim()}>Add</button>
        </div>
        {sectors.length > 0 && (
          <div className="chip-grid">
            {sectors.map((s, i) => (
              <span key={i} className="tag">
                {s}
                <span className="tag-remove" onClick={() => removeSector(i)}>×</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="actions">
        <button className="btn" onClick={onBack}>← Back</button>
        <button className="btn btn-primary" onClick={onNext} disabled={!valid}>
          Build keyword matrix →
        </button>
      </div>
    </div>
  );
}
